// ** react imports
import React, { useEffect, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';

// ** router imports
import { Link } from 'react-router-dom';

// ** redux imports
import { useSelector } from 'react-redux';

// ** custom hooks imports
import {
  useGetAllUsers,
  useLogoutAllUsers,
} from '../../../hooks/apis/useUsers';

// ** components imports
import ModalBasic from '../../../components/ModalBasic';
import DeleteButton from '../../../partials/actions/DeleteButton';
import NormalButton from '../../../components/common/Buttons/NormalButton/index';
import PasswordInput from '../../../components/common/Inputs/PasswordInput';
import LoadingButton from '../../../components/common/Buttons/LoadingButton';

// ** views imports
import UsersTable from '../../../views/users/UsersList/UsersTable';
import UserRoleSelect from '../../../views/users/UsersList/UserRoleSelect';
import UserFilterButton from '../../../views/users/UsersList/UserFilterButton';
import UsersPagination from '../../../views/users/UsersList/UsersPagination';

// ** enums imports
import { BUTTON_THEMES, USER_ROLES } from '../../../enums/common';

function UserList() {
  // Redux state
  const { token, users } = useSelector(state => state.users);

  // states
  const [selectedItems, setSelectedItems] = useState([]);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    roles: `${USER_ROLES.ADMIN},${USER_ROLES.MANAGER}` || USER_ROLES.ADMIN,
    sortBy: 'userId',
    sortOrder: 'asc',
    isActive: undefined,
    search: '',
    isEmailVerified: undefined,
    isPasswordSet: undefined,
  });

  // Modal states
  const [showLogoutAllModal, setShowLogoutAllModal] = useState(false);

  // React hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // custom hooks
  const { data, isGetAllUsersLoading, isGetAllUsersFetching, getAllUsers } =
    useGetAllUsers();

  // Logout all users hook
  const { logoutAllUsers, isLogoutAllUsersLoading } = useLogoutAllUsers();

  // fetch users whenever query changes
  useEffect(() => {
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    getAllUsers(cleanQuery);
  }, [query]);

  // normalize users data for table and pagination
  const tableData = useMemo(() => {
    return {
      data: data?.data || [],
      total: data?.total || 0,
      currentPage: query.page,
      totalPages: Math.ceil((data?.total || 0) / query.limit),
    };
  }, [data, query.page, query.limit]);

  // handlers
  const handleSelectedItems = selectedItems => {
    setSelectedItems([...selectedItems]);
  };

  const handleRoleChange = roleFilter => {
    setQuery(prev => ({
      ...prev,
      roles: roleFilter,
      page: 1,
    }));
  };

  const handleStatusFilter = statusFilters => {
    setQuery(prev => ({
      ...prev,
      ...statusFilters,
      page: 1,
    }));
  };

  const handleSearchFilter = searchTerm => {
    setQuery(prev => ({
      ...prev,
      search: searchTerm,
      page: 1,
    }));
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setQuery(prev => ({
      ...prev,
      sortBy,
      sortOrder,
    }));
  };

  const handlePageChange = page => {
    setQuery(prev => ({
      ...prev,
      page,
    }));
  };

  const handleLimitChange = limit => {
    setQuery(prev => ({
      ...prev,
      limit,
      page: 1,
    }));
  };

  const handleResetFilters = () => {
    setQuery({
      page: 1,
      limit: 10,
      roles: `${USER_ROLES.ADMIN},${USER_ROLES.MANAGER}`,
      sortBy: 'phone',
      sortOrder: 'desc',
      isActive: undefined,
      search: '',
      isEmailVerified: undefined,
      isPasswordSet: undefined,
    });
  };

  // Handle logout all users
  const handleLogoutAllUsers = async data => {
    const response = await logoutAllUsers(data);
    if (response.data?.success) {
      setShowLogoutAllModal(false);
      reset();
    }
  };

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            {/* Left: Title */}
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Users
              </h1>
            </div>

            {/* Right: Actions */}
            <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
              {/* Delete button */}
              <DeleteButton selectedItems={selectedItems} />

              {/* Role Filter */}
              <UserRoleSelect
                onChange={handleRoleChange}
                currentValue={query.roles}
              />

              {/* Advanced Filter button */}
              <UserFilterButton
                align="right"
                onStatusFilter={handleStatusFilter}
                onSearchFilter={handleSearchFilter}
                onReset={handleResetFilters}
                currentFilters={{
                  isActive: query.isActive,
                  isEmailVerified: query.isEmailVerified,
                  isPasswordSet: query.isPasswordSet,
                  search: query.search,
                }}
              />

              {/* Add user button */}
              {token && users?.role === USER_ROLES.ADMIN && (
                <Link to="/users/add">
                  <NormalButton theme={BUTTON_THEMES.PRIMARY}>
                    <svg
                      className="fill-current shrink-0 xs:hidden"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                    >
                      <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                    </svg>
                    <span className="max-xs:sr-only">Add User</span>
                  </NormalButton>
                </Link>
              )}

              {token && users?.role === USER_ROLES.ADMIN && (
                <NormalButton
                  theme={BUTTON_THEMES.DANGER}
                  onClick={() => setShowLogoutAllModal(true)}
                >
                  <svg
                    className="fill-current shrink-0 xs:hidden"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                  </svg>
                  <span className="max-xs:sr-only">Logout All</span>
                </NormalButton>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {(query.search ||
            query.isEmailVerified !== undefined ||
            query.isPasswordSet !== undefined ||
            query.isActive !== undefined ||
            query.roles !== `${USER_ROLES.ADMIN},${USER_ROLES.MANAGER}`) && (
            <div className="mb-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 text-sm text-violet-700 dark:text-violet-300">
                  <svg
                    className="w-4 h-4 mt-0.5 sm:mt-0 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-medium whitespace-nowrap">
                      Active Filters:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {query.search && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Search: &quot;
                          {query.search.length > 20
                            ? query.search.substring(0, 20) + '...'
                            : query.search}
                          &quot;
                        </span>
                      )}
                      {query.roles !==
                        `${USER_ROLES.ADMIN},${USER_ROLES.MANAGER}` && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Role:{' '}
                          {query.roles === USER_ROLES.ADMIN
                            ? 'Admin Only'
                            : query.roles === USER_ROLES.MANAGER
                              ? 'Manager Only'
                              : 'Custom'}
                        </span>
                      )}
                      {query.isEmailVerified !== undefined && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Email Verified: {query.isEmailVerified ? 'Yes' : 'No'}
                        </span>
                      )}
                      {query.isPasswordSet !== undefined && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Password Set: {query.isPasswordSet ? 'Yes' : 'No'}
                        </span>
                      )}
                      {query.isActive && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Status: {query.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetFilters}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium whitespace-nowrap transition-colors self-start sm:self-center"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <UsersTable
            data={tableData}
            selectedItems={handleSelectedItems}
            isGetAllUsersFetching={isGetAllUsersFetching}
            isGetAllUsersLoading={isGetAllUsersLoading}
            onSortChange={handleSortChange}
            currentSortBy={query.sortBy}
            currentSortOrder={query.sortOrder}
          />

          {/* Pagination */}
          <div className="mt-6">
            <UsersPagination
              currentPage={tableData.currentPage}
              totalPages={tableData.totalPages}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              currentLimit={query.limit}
              totalItems={tableData.total}
            />
          </div>
        </div>
      </main>

      {/* Logout All Modal */}
      <ModalBasic
        title="Logout All Users"
        modalOpen={showLogoutAllModal}
        setModalOpen={setShowLogoutAllModal}
      >
        <div className="p-5">
          <div className="mb-4">
            <p className="text-sm text-gray-800 dark:text-gray-100">
              Enter your password to confirm logout of all users.
            </p>
          </div>

          <div className="mb-4">
            <PasswordInput
              label="Password"
              name="password"
              register={register}
              errors={errors.password}
              placeholder="Enter your password"
              type="password"
            />
          </div>

          <div className="flex justify-end items-center mt-6 gap-2">
            <NormalButton
              type="button"
              theme={BUTTON_THEMES.DANGER}
              onClick={() => setShowLogoutAllModal(false)}
            >
              Cancel
            </NormalButton>
            <LoadingButton
              type="button"
              theme={BUTTON_THEMES.PRIMARY}
              isLoading={isLogoutAllUsersLoading}
              onClick={handleSubmit(handleLogoutAllUsers)}
            >
              Logout
            </LoadingButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
}

export default UserList;
